import { MAP_RENDERER_PADDING } from 'components/map/map';
import { useBilikZonesLayerQuery } from 'generated/graphql';
import { FeatureCollection } from 'geojson';
import L from 'leaflet';
import { FunctionComponent, useEffect } from 'react';
import { useMap } from 'react-leaflet';
import { bilikZoneColor, bilikZoneFillColor } from 'utils/geometry';
import './bilik-zones-layer.css';

// Hoverable zones: no pointer cursor, see the css file
export const BILIK_ZONE_CLASS_NAME = 'bilik-zone';

const BILIK_ZONES_PANE = 'bilik-zones';
// Below the default overlay pane (400): pros markers and interventions stay on top
const BILIK_ZONES_PANE_Z_INDEX = '350';

// The layer is added to the map directly instead of being a child of <Map />
// so that the map doesn't fit its bounds on the whole France
const BilikZonesLayer: FunctionComponent = () => {
  const map = useMap();
  const { data } = useBilikZonesLayerQuery();

  useEffect(() => {
    if (!data) return;

    const pane =
      map.getPane(BILIK_ZONES_PANE) ?? map.createPane(BILIK_ZONES_PANE);
    pane.style.zIndex = BILIK_ZONES_PANE_Z_INDEX;

    const featureCollection: FeatureCollection = {
      type: 'FeatureCollection',
      features: data.bilikZoneOutline.flatMap((zone) =>
        zone.outline
          ? [
              {
                type: 'Feature',
                geometry: zone.outline,
                properties: { name: zone.bilikZone?.name },
              },
            ]
          : [],
      ),
    };

    // The renderer created by the map for the pane has the default padding
    const renderer = L.svg({
      pane: BILIK_ZONES_PANE,
      padding: MAP_RENDERER_PADDING,
    });

    // Typed by hand: the GeoJSON options are not typed with the path ones,
    // given to each zone layer
    const options: L.GeoJSONOptions & L.PathOptions = {
      pane: BILIK_ZONES_PANE,
      renderer,
      pmIgnore: true,
      // Same style as the other zones on the bilik zone form map
      style: {
        color: bilikZoneColor,
        weight: 1,
        fillColor: bilikZoneFillColor,
        fillOpacity: 0.2,
        className: BILIK_ZONE_CLASS_NAME,
      },
      // Interactive for the tooltip only: clicks bubble up to the map, so
      // map click handlers (e.g. city selection) keep working
      bubblingMouseEvents: true,
      onEachFeature: (feature, featureLayer) => {
        featureLayer.bindTooltip(feature.properties.name, { sticky: true });
      },
    };

    const layer = L.geoJSON(featureCollection, options);

    layer.addTo(map);

    return () => {
      layer.remove();
      renderer.remove();
    };
  }, [map, data]);

  return null;
};

export default BilikZonesLayer;
